// Minimal HTML renderer for the publish pipeline.
// Pure functions, no DOM dependency. Same API the future packages/renderer will expose.

export type Theme = {
  colors: Record<string, string>;
  fonts?: { sans?: { family: string; weights: number[]; subsets: string[] } };
  spacing?: number[];
  radii?: number[];
  shadows?: string[];
};

export type Block = {
  id: string;
  componentKey: string;
  props: Record<string, unknown>;
  mobile?: { props?: Record<string, unknown> };
  visibility?: { desktop?: boolean; mobile?: boolean };
};

export type PageDoc = {
  id: string;
  slug: string;
  parent_id: string | null;
  title: string;
  revision: {
    metadata: PageMetadata;
    header: { inherit?: true } | { componentKey: string; props: Record<string, unknown> };
    footer: { inherit?: true } | { componentKey: string; props: Record<string, unknown> };
    blocks: Block[];
  } | null;
};

export type PageMetadata = {
  title: string;
  slug: string;
  description?: string;
  canonical?: string | null;
  noindex?: boolean;
  og?: {
    title?: string;
    description?: string;
    image?: string;       // absolute or relative URL
    type?: string;        // website | article | product | …
    siteName?: string;
    locale?: string;
    twitterCard?: "summary" | "summary_large_image";
    twitterSite?: string; // @handle
    twitterCreator?: string;
  };
  behavior?: { pageStyle?: string; showFooterCta?: boolean; showPageHeader?: boolean; customCssClass?: string; redirectTo?: string; excludeFromBuild?: boolean };
};

export type SiteSettings = {
  gtmContainerId?: string;
  metaTitleTemplate?: string;
  metaDescriptionFallback?: string;
  ogTemplate?: { background?: string; title?: { fontFamily?: string; color?: string; fontSize?: number } } | null;
  cspOverrides?: { scriptSrc?: string[]; styleSrc?: string[]; connectSrc?: string[]; imgSrc?: string[]; frameSrc?: string[] };
  pwaEnabled?: boolean;
  chrome?: SiteChrome;
};

export type SiteChrome = {
  siteName?: string;
  header?: { cta_text?: string; cta_link?: string };
  footer?: {
    cta_heading?: string; cta_description?: string; cta_button_text?: string; cta_button_link?: string;
    tagline?: string;
    columns?: Array<{ type: string; value: { heading?: string; links?: Array<{ label: string; url: string }> } }>;
    copyright_text?: string;
    trust_badges?: Array<{ value: { icon: string; label: string } }>;
    legal_links?: Array<{ value: { label: string; url: string } }>;
  };
  contact?: {
    contact_methods?: Array<{ value: { type: string; label?: string; value: string } }>;
    social_links?: Array<{ value: { url: string; icon: string; label: string } }>;
  };
  nav?: Array<{ slug: string; title: string }>;
};

export function renderHeader(chrome: SiteChrome | undefined, currentSlug?: string): string {
  if (!chrome) return "";
  const name = chrome.siteName || "neuera.care";
  const cta = chrome.header?.cta_text || "Book a Consult";
  const ctaLink = chrome.header?.cta_link || "#book";
  const nav = chrome.nav ?? [];
  return `<header class="nl-header" id="main-header">
  <div class="container nl-header-inner" id="header-inner">
    <a href="/" class="nl-header-logo"><span>${escapeHtml(name)}</span></a>
    <nav class="nl-header-nav d-none d-md-flex">
      ${nav.map((n) => `<a href="/${escapeHtml(n.slug)}/" class="nl-header-link${n.slug === currentSlug ? " active" : ""}">${escapeHtml(n.title)}</a>`).join("")}
    </nav>
    <div class="d-flex align-items-center gap-3">
      <a href="${escapeHtml(ctaLink)}" class="nl-btn nl-btn-primary nl-header-cta d-none d-md-inline-flex" data-track-click="BAOC-header" data-track-category="cta" data-track-label="header">${escapeHtml(cta)}</a>
      <button class="nl-header-toggle d-md-none" id="mobile-menu-btn" aria-label="Toggle menu"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg></button>
    </div>
  </div>
  <div class="nl-mobile-menu" id="mobile-menu">
    <nav class="container">
      ${nav.map((n) => `<a href="/${escapeHtml(n.slug)}/" class="nl-mobile-link">${escapeHtml(n.title)}</a>`).join("")}
      <a href="${escapeHtml(ctaLink)}" class="nl-btn nl-btn-primary nl-mobile-cta" data-track-click="BAOC-header-mobile" data-track-category="cta" data-track-label="header-mobile">${escapeHtml(cta)}</a>
    </nav>
  </div>
</header>`;
}

export function renderFooter(chrome: SiteChrome | undefined, opts?: { showFooterCta?: boolean }): string {
  if (!chrome?.footer) return "";
  const f = chrome.footer;
  const c = chrome.contact;
  const showCta = opts?.showFooterCta !== false;
  const year = new Date().getFullYear();
  const ctaBlock = showCta && f.cta_heading ? `<section class="nl-footer-cta" data-track-section="footer-cta">
    <div class="container text-center">
      <h2>${escapeHtml(f.cta_heading)}</h2>
      ${f.cta_description ? `<p>${escapeHtml(f.cta_description)}</p>` : ""}
      <a href="${escapeHtml(f.cta_button_link || "#book")}" class="nl-btn nl-btn-white" data-track-click="BAOC-footer-cta" data-track-category="cta" data-track-label="footer">${escapeHtml(f.cta_button_text || "Book a Consult")}</a>
    </div>
  </section>` : "";

  const columnsHtml = (f.columns ?? []).map((col) => {
    if (col.type !== "link_column") return "";
    const links = col.value.links ?? [];
    return `<div class="col-md-3 mb-4">
      <p class="nl-footer-heading">${escapeHtml(col.value.heading ?? "")}</p>
      <ul class="list-unstyled">
        ${links.map((l) => `<li class="mb-2"><a href="${escapeHtml(l.url)}">${escapeHtml(l.label)}</a></li>`).join("")}
      </ul>
    </div>`;
  }).join("");

  const contactBlock = c ? `<div class="col-md-3 mb-4">
    <p class="nl-footer-heading">Contact</p>
    <ul class="list-unstyled">
      ${(c.contact_methods ?? []).map(({ value: m }) => {
        const label = m.label ?? m.value;
        if (m.type === "email") return `<li class="mb-2"><a href="mailto:${escapeHtml(m.value)}">${escapeHtml(label)}</a></li>`;
        if (m.type === "phone") return `<li class="mb-2"><a href="tel:${escapeHtml(m.value)}">${escapeHtml(label)}</a></li>`;
        if (m.type === "whatsapp") return `<li class="mb-2"><a href="https://wa.me/${escapeHtml(m.value)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a></li>`;
        return `<li class="mb-2">${escapeHtml(label)}</li>`;
      }).join("")}
    </ul>
  </div>` : "";

  const socialBlock = c?.social_links?.length ? `<div class="nl-footer-social">
    ${(c.social_links ?? []).map(({ value: s }) => `<a href="${escapeHtml(s.url)}" aria-label="${escapeHtml(s.label)}" target="_blank" rel="noopener noreferrer"><i class="bi ${escapeHtml(s.icon)}"></i></a>`).join("")}
  </div>` : "";

  return `${ctaBlock}<footer class="nl-footer">
  <div class="container">
    <div class="row">
      <div class="col-md-3 mb-4">
        <div class="nl-footer-brand">${escapeHtml(chrome.siteName || "neuera.care")}</div>
        ${f.tagline ? `<p class="nl-footer-tagline">${escapeHtml(f.tagline)}</p>` : ""}
        ${socialBlock}
      </div>
      ${columnsHtml}
      ${contactBlock}
    </div>
    <div class="nl-footer-bottom">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <small>&copy; ${year} ${escapeHtml(f.copyright_text ?? "neuera.care. All rights reserved.")}</small>
        ${f.trust_badges?.length ? `<div class="d-flex gap-3">${f.trust_badges.map(({ value: b }) => `<small class="nl-footer-trust"><i class="bi ${escapeHtml(b.icon)}"></i> ${escapeHtml(b.label)}</small>`).join("")}</div>` : ""}
      </div>
    </div>
  </div>
</footer>`;
}

export type Site = { id: string; slug: string; name: string; primary_domain?: string | null };

export function buildPagePath(p: PageDoc, all: PageDoc[]): string {
  if (p.slug === "__404") return "/404.html";
  if (p.slug === "__500") return "/500.html";
  if (p.slug === "home" && !p.parent_id) return "/index.html";
  const parts: string[] = [];
  let cur: PageDoc | undefined = p;
  while (cur) {
    if (cur.slug && cur.slug !== "home") parts.unshift(cur.slug);
    cur = cur.parent_id ? all.find((x) => x.id === cur!.parent_id) : undefined;
  }
  if (parts.length === 0) return "/index.html";
  return `/${parts.join("/")}/index.html`;
}

export function interpolate(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{site\.([a-zA-Z][a-zA-Z0-9_]*)\}\}/g, (m, k) => (k in vars ? escapeHtml(vars[k]!) : m));
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" })[ch]!);
}

export function renderPage(opts: {
  site: Site;
  page: PageDoc;
  allPages: PageDoc[];
  theme: Theme | null;
  variables: Record<string, string>;
  assetUrl: (id: string) => string;
  settings?: SiteSettings;
  origin?: string;
}) {
  const { site, page, theme, variables } = opts;
  if (!page.revision) return null;
  const md = page.revision.metadata;
  const blocks = page.revision.blocks;
  const path = buildPagePath(page, opts.allPages);

  const settings = opts.settings ?? {};
  const origin = opts.origin ?? (site.primary_domain ? `https://${site.primary_domain}` : "");

  const titleTemplate = settings.metaTitleTemplate || "%title%";
  const pageTitle = interpolate(md.title || page.title, variables);
  const fullTitle = titleTemplate.replace("%title%", pageTitle).replace("%site%", site.name);
  const description = interpolate(md.description || settings.metaDescriptionFallback || "", variables);
  const canonical = md.canonical
    ? md.canonical
    : origin
      ? `${origin}${path.replace(/index\.html$/, "")}`
      : null;

  const og = md.og ?? {};
  const ogTitle = interpolate(og.title || pageTitle, variables);
  const ogDesc = interpolate(og.description || description, variables);
  const ogImage = og.image ? absUrl(og.image, origin, opts.assetUrl) : null;
  const ogType = og.type || "website";
  const ogSiteName = og.siteName || site.name;
  const ogLocale = og.locale || "en_US";
  const twitterCard: "summary" | "summary_large_image" = og.twitterCard || (ogImage ? "summary_large_image" : "summary");

  const css = renderThemeCss(theme) + renderBaseCss();
  const blocksHtml = blocks
    .filter((b) => b.visibility?.desktop !== false)
    .map((b) => renderBlock(b, "desktop", variables, opts.assetUrl))
    .join("\n");

  const mobileOverrides = renderMobileOverrides(blocks, variables, opts.assetUrl);

  const gtmId = (settings.gtmContainerId || "").trim();
  const gtmHead = gtmId
    ? `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${escapeHtml(gtmId)}');</script>`
    : "";
  const gtmBody = gtmId
    ? `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${escapeHtml(gtmId)}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`
    : "";

  const head = [
    `<meta charset="utf-8" />`,
    `<meta name="viewport" content="width=device-width, initial-scale=1" />`,
    `<title>${escapeHtml(fullTitle)}</title>`,
    description && `<meta name="description" content="${escapeHtml(description)}" />`,
    canonical && `<link rel="canonical" href="${escapeHtml(canonical)}" />`,
    md.noindex && `<meta name="robots" content="noindex, nofollow" />`,
    // Fonts + icons + bundled stylesheet
    `<link rel="preconnect" href="https://fonts.googleapis.com" />`,
    `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />`,
    `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Source+Sans+3:ital,wght@0,300..700;1,300..700&display=swap" />`,
    `<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css" />`,
    `<link rel="stylesheet" href="/assets/neuera.css" />`,
    `<link rel="stylesheet" href="/assets/grid-shim.css" />`,
    `<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4" defer></script>`,
    // Open Graph
    `<meta property="og:type" content="${escapeHtml(ogType)}" />`,
    `<meta property="og:site_name" content="${escapeHtml(ogSiteName)}" />`,
    `<meta property="og:title" content="${escapeHtml(ogTitle)}" />`,
    ogDesc && `<meta property="og:description" content="${escapeHtml(ogDesc)}" />`,
    canonical && `<meta property="og:url" content="${escapeHtml(canonical)}" />`,
    ogImage && `<meta property="og:image" content="${escapeHtml(ogImage)}" />`,
    `<meta property="og:locale" content="${escapeHtml(ogLocale)}" />`,
    // Twitter
    `<meta name="twitter:card" content="${escapeHtml(twitterCard)}" />`,
    og.twitterSite && `<meta name="twitter:site" content="${escapeHtml(og.twitterSite)}" />`,
    og.twitterCreator && `<meta name="twitter:creator" content="${escapeHtml(og.twitterCreator)}" />`,
    `<meta name="twitter:title" content="${escapeHtml(ogTitle)}" />`,
    ogDesc && `<meta name="twitter:description" content="${escapeHtml(ogDesc)}" />`,
    ogImage && `<meta name="twitter:image" content="${escapeHtml(ogImage)}" />`,
    gtmHead,
    `<style>${css}</style>`,
    mobileOverrides && `<style>${mobileOverrides}</style>`,
  ].filter(Boolean).join("\n");

  const html = `<!doctype html>
<html lang="en">
<head>
${head}
</head>
<body class="${escapeHtml(md.behavior?.customCssClass ?? "")}">
${gtmBody}
${md.behavior?.pageStyle === "landing" ? "" : renderHeader(settings.chrome, page.slug)}
  <main>${blocksHtml}</main>
${md.behavior?.pageStyle === "landing" ? "" : renderFooter(settings.chrome, { showFooterCta: md.behavior?.showFooterCta !== false })}
  <script src="/assets/neuera.js" defer></script>
</body>
</html>`;

  return { path, html };
}

function absUrl(url: string, origin: string, assetUrl: (id: string) => string): string {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  // Asset ID heuristic: looks like ULID-ish (no slashes, no extension)
  if (!/[./]/.test(url) && url.length >= 16) return assetUrl(url);
  if (url.startsWith("/")) return origin ? origin + url : url;
  return origin ? `${origin}/${url}` : url;
}

function renderThemeCss(theme: Theme | null): string {
  if (!theme) return "";
  const colors = theme.colors ?? {};
  const parts: string[] = [];
  for (const [k, v] of Object.entries(colors)) parts.push(`--c-${k}: ${v};`);
  if (theme.fonts?.sans) parts.push(`--font-sans: "${theme.fonts.sans.family}", system-ui, sans-serif;`);
  const family = theme.fonts?.sans?.family;
  const gFont = family ? `@import url('https://fonts.googleapis.com/css2?family=${family.replaceAll(" ", "+")}:wght@400;500;600;700&display=swap');` : "";
  return `${gFont}\n:root { ${parts.join(" ")} }`;
}

function renderBaseCss(): string {
  return `
*, *::before, *::after { box-sizing: border-box; }
body { margin: 0; font-family: var(--font-sans, system-ui, sans-serif); color: var(--c-ink, #0c0c0c); background: var(--c-ground, #fff); line-height: 1.5; }
img { max-width: 100%; height: auto; }
a { color: var(--c-primary, #2563eb); }
.cms-section { padding: 48px 24px; max-width: 1100px; margin: 0 auto; }
.cms-hero-centered { padding: 80px 24px; text-align: center; max-width: 900px; margin: 0 auto; }
.cms-hero-centered h1 { font-size: clamp(28px, 5vw, 48px); margin: 0 0 16px; }
.cms-hero-centered p { font-size: 18px; color: var(--c-muted, #6b7280); }
.cms-hero-split { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: center; padding: 64px 24px; max-width: 1100px; margin: 0 auto; }
.cms-hero-split h1 { font-size: clamp(24px, 4vw, 36px); margin: 0 0 12px; }
.cms-hero-split .cta { display: inline-block; background: var(--c-primary, #2563eb); color: #fff; padding: 10px 18px; border-radius: 6px; text-decoration: none; }
.cms-content-section { padding: 48px 24px; max-width: 900px; margin: 0 auto; }
.cms-spacer-small { height: 16px; }
.cms-spacer-medium { height: 32px; }
.cms-spacer-large { height: 64px; }
.cms-spacer-xlarge { height: 96px; }
.cms-divider { border-top: 1px solid #e5e7eb; }
.cms-cards-grid { display: grid; grid-template-columns: repeat(var(--cols, 3), minmax(0, 1fr)); gap: 24px; padding: 48px 24px; max-width: 1100px; margin: 0 auto; }
.cms-card { background: #fff; padding: 24px; border-radius: 8px; border: 1px solid #e5e7eb; }
.cms-feature-split { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; padding: 48px 24px; max-width: 1100px; margin: 0 auto; align-items: center; }
.cms-feature-split.image-right { grid-template-areas: "text image"; }
.cms-feature-split.image-left  { grid-template-areas: "image text"; }
.cms-feature-split .text { grid-area: text; }
.cms-feature-split .image { grid-area: image; }
.cms-form { padding: 48px 24px; max-width: 600px; margin: 0 auto; }
.cms-form label { display: block; margin-bottom: 12px; }
.cms-form .field { width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; }
.cms-form button { background: var(--c-primary, #2563eb); color: #fff; border: 0; padding: 10px 18px; border-radius: 6px; cursor: pointer; }
@media (max-width: 767px) {
  .cms-hero-split { grid-template-columns: 1fr; padding: 40px 16px; }
  .cms-feature-split { grid-template-columns: 1fr; grid-template-areas: "text" "image" !important; padding: 32px 16px; }
  .cms-cards-grid { grid-template-columns: 1fr; padding: 32px 16px; }
}`;
}

// URL-or-asset-id helper. Wagtail-seeded image fields hold full URLs;
// editor-created blocks hold opaque CMS-Plus asset IDs. Detect and route.
function imgSrc(val: unknown, assetUrl: (id: string) => string): string {
  if (!val) return "";
  const s = String(val);
  if (/^https?:\/\//i.test(s) || s.startsWith("/") || s.startsWith("data:")) return s;
  return assetUrl(s);
}

export function renderBlock(b: Block, viewport: "desktop" | "mobile", vars: Record<string, string>, assetUrl: (id: string) => string): string {
  const p = b.props as Record<string, unknown>;
  const get = (k: string) => interpolate(String(p[k] ?? ""), vars);
  const key = b.componentKey;

  // Legacy generic kinds — kept for backwards compatibility
  if (key === "hero.centered") return `<section class="nl-hero nl-hero--center"><div class="container nl-hero-content"><div class="nl-hero-text"><h1 class="nl-hero-heading">${esc(get("title"))}</h1>${get("subtitle") ? `<p class="nl-hero-subheading">${esc(get("subtitle"))}</p>` : ""}</div></div></section>`;
  if (key === "hero.split") {
    const ctaText = get("ctaText"); const ctaLink = get("ctaLink");
    return `<section class="nl-hero nl-hero-compact"><div class="container nl-hero-content"><div class="nl-hero-text"><h1 class="nl-hero-heading">${esc(get("title"))}</h1>${get("body") ? `<p class="nl-hero-subheading">${esc(get("body"))}</p>` : ""}${ctaText ? `<div class="nl-hero-actions"><a href="${esc(ctaLink || "#")}" class="nl-btn nl-btn-primary"><span>${esc(ctaText)}</span></a></div>` : ""}</div></div></section>`;
  }
  if (key === "cards.grid") {
    const items = Array.isArray(p.items) ? (p.items as Array<{ heading?: string; body?: string }>) : [];
    const cols = Number(p.columns ?? 3);
    const gridCls = cols === 2 ? "nl-grid-2" : cols === 4 ? "nl-grid-4" : "nl-grid-3";
    return `<section class="nl-section"><div class="container"><div class="${gridCls}">${items.map((it) => `<div class="nl-card"><div class="nl-card-body"><h3 class="nl-card-title">${esc(it.heading ?? "")}</h3>${it.body ? `<p class="nl-card-text">${esc(it.body)}</p>` : ""}</div></div>`).join("")}</div></div></section>`;
  }

  // ── neuera-faithful renderers ──────────────────────────────────────
  if (key === "hero.neuera") return renderNeueraHero(p, vars, assetUrl);
  if (key === "trust_strip") return renderTrustStrip(p);
  if (key === "cards.neuera") return renderNeueraCards(p, vars, assetUrl);
  if (key === "features") return renderFeatures(p, vars);
  if (key === "testimonials") return renderTestimonials(p, vars, assetUrl);
  if (key === "steps") return renderSteps(p, vars, assetUrl);
  if (key === "faq") return renderFaq(p, vars);
  if (key === "cta") return renderCta(p, vars);
  if (key === "programs_list") return renderProgramsList(p, vars);
  if (key === "services") return renderServices(p, vars);
  if (key === "pricing") return renderPricing(p, vars);
  if (key === "office_info") return renderOfficeInfo(p, vars);
  if (key === "contact_form") return renderContactForm(p, vars);
  if (key === "newsletter") return renderNewsletter(p, vars);

  if (key === "content.section") {
    const paragraphs = Array.isArray(p.paragraphs) ? (p.paragraphs as string[]) : [];
    const bgCls = p.background === "warm" ? " nl-section-warm" : p.background === "dark" ? " nl-section-dark" : "";
    return `<section class="nl-section${bgCls}${!p.heading ? " nl-section--no-heading" : ""}"><div class="container"><div class="row justify-content-center"><div class="col-lg-8 reveal">${p.heading ? `<h2 class="mb-4">${esc(get("heading"))}</h2>` : ""}${paragraphs.map((para) => para ? `<p style="color:var(--nl-text-muted); line-height:1.85; font-size:1.1rem;">${esc(interpolate(para, vars))}</p>` : "").join("")}</div></div></div></section>`;
  }
  if (key === "content.richtext") return `<section class="nl-section"><div class="container"><div class="row justify-content-center"><div class="col-lg-8 reveal nl-prose">${sanitize(String(p.html ?? p.content ?? ""), vars)}</div></div></div></section>`;
  if (key === "content.feature_split") {
    const reverse = String(p.imagePosition ?? p.image_position ?? "right") === "left";
    const img = (p.image || p.assetId) ? imgSrc(p.image || p.assetId, assetUrl) : "";
    const ctaText = String(p.ctaText ?? p.cta_text ?? "");
    const ctaLink = String(p.ctaLink ?? p.cta_link ?? "");
    return `<section class="nl-section"><div class="container"><div class="nl-feature-split${reverse ? " nl-feature-split-reverse" : ""} reveal"><div class="nl-feature-split-content"><h2>${esc(get("heading"))}</h2><div class="nl-prose mt-3">${sanitize(String(p.description ?? ""), vars)}</div>${ctaText ? `<a href="${esc(ctaLink)}" class="nl-btn nl-btn-primary mt-3">${esc(ctaText)}</a>` : ""}</div>${img ? `<div class="nl-feature-split-img"><img src="${esc(img)}" alt="${esc(get("heading"))}" loading="lazy" /></div>` : ""}</div></div></section>`;
  }
  if (key === "content.image") {
    const asset = p.image ? imgSrc(p.image, assetUrl) : p.assetId ? imgSrc(p.assetId, assetUrl) : "";
    const alt = String(p.alt ?? p.alt_text ?? "");
    return `<section class="nl-section"><div class="container"><figure class="nl-image-figure">${asset ? `<img src="${esc(asset)}" alt="${esc(alt)}" loading="lazy" />` : ""}${p.caption ? `<figcaption>${esc(get("caption"))}</figcaption>` : ""}</figure></div></section>`;
  }
  if (key === "form.builder") {
    const fields = Array.isArray(p.fields) ? (p.fields as Array<{ kind: string; name: string; label: string; required?: boolean; placeholder?: string }>) : [];
    return `<section class="nl-section"><div class="container"><div class="row justify-content-center"><div class="col-lg-8 reveal"><div class="nl-form-wrapper">${p.title ? `<h2 class="text-center mb-3">${esc(get("title"))}</h2>` : ""}${p.description ? `<p class="text-center lead mb-4">${esc(get("description"))}</p>` : ""}<form class="contact-form" data-form-id="${esc(b.id)}" data-track-form="contact-form" novalidate>${fields.map((f) => fieldHtml(f)).join("")}<button type="submit" class="nl-btn nl-btn-primary mt-3">${esc((p.submit as { label?: string } | undefined)?.label ?? "Send")}</button></form></div></div></div></div></section>`;
  }
  if (key === "layout.spacer") {
    const style = String(p.style ?? "blank");
    const height = String(p.height ?? "medium");
    const map: Record<string, string> = { small: "1rem", medium: "2rem", large: "4rem", xlarge: "6rem" };
    if (style === "divider") return `<div class="container"><hr class="nl-divider" style="margin:${map[height] ?? "2rem"} auto" /></div>`;
    if (style === "dots") return `<div class="container" style="text-align:center;padding:${map[height] ?? "2rem"} 0;color:var(--nl-text-muted)">· · ·</div>`;
    return `<div style="height:${map[height] ?? "2rem"}"></div>`;
  }
  return `<!-- unknown block: ${esc(key)} -->`;
  void viewport;
}

// Helpers used by neuera renderers
function esc(s: string): string { return escapeHtml(s ?? ""); }
function slugify(s: string): string { return String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }

function renderNeueraHero(p: Record<string, unknown>, vars: Record<string, string>, assetUrl: (id: string) => string): string {
  const heading = interpolate(String(p.heading ?? ""), vars);
  const sub = interpolate(String(p.subheading ?? ""), vars);
  const badge = String(p.badge_text ?? "");
  const cta = String(p.cta_text ?? "");
  const ctaLink = String(p.cta_link ?? "");
  const cta2 = String(p.secondary_cta_text ?? "");
  const cta2Link = String(p.secondary_cta_link ?? "");
  const dark = String(p.theme ?? "light") === "dark";
  const bgImage = p.background_image ? imgSrc(p.background_image, assetUrl) : "";
  const trackSlug = slugify(heading) || "hero";
  const content = `${badge ? `<div class="nl-hero-badge">${esc(badge)}</div>` : ""}<h1 class="nl-hero-heading">${heading}</h1>${sub ? `<p class="nl-hero-subheading">${sub}</p>` : ""}${(cta || cta2) ? `<div class="nl-hero-actions">${cta ? `<a href="${esc(ctaLink || "#book")}" class="nl-btn nl-btn-primary" data-track-click="hero-${esc(trackSlug)}-primary" data-track-category="hero" data-track-label="${esc(trackSlug)}"><span>${esc(cta)}</span></a>` : ""}${cta2 ? `<a href="${esc(cta2Link)}" class="nl-btn nl-btn-outline" data-track-click="hero-${esc(trackSlug)}-secondary" data-track-category="hero" data-track-label="${esc(trackSlug)}">${esc(cta2)}</a>` : ""}</div>` : ""}`;
  const classes = ["nl-hero"];
  if (dark) classes.push("nl-hero--dark");
  if (!bgImage && !cta) classes.push("nl-hero-compact");
  if (!bgImage) classes.push("nl-hero--center");
  return `<section class="${classes.join(" ")}" data-track-section="hero-${esc(trackSlug)}"><div class="nl-blob nl-blob-1"></div><div class="nl-blob nl-blob-2"></div><div class="nl-blob nl-blob-3"></div><div class="nl-hero-dotgrid"></div><div class="container nl-hero-content">${bgImage ? `<div class="nl-hero-split-grid"><div class="nl-hero-text">${content}</div><div class="nl-hero-img-col"><img src="${esc(bgImage)}" alt="${esc(heading)}" class="nl-hero-img" loading="eager" /></div></div>` : `<div class="nl-hero-text">${content}</div>`}</div></section>`;
}

function renderTrustStrip(p: Record<string, unknown>): string {
  const items = Array.isArray(p.items) ? p.items as Array<{ icon?: string; label?: string }> : [];
  const style = String(p.style ?? "default");
  if (style === "pills") {
    return `<section class="nl-trust-bar nl-trust-bar--pills${p.background === "warm" ? " nl-section-warm" : ""}" data-track-section="trust-strip"><div class="container"><div class="nl-trust-bar-grid">${items.map((it) => `<span class="nl-pill">${it.icon ? `<i class="bi ${esc(it.icon)}" aria-hidden="true"></i>` : ""} ${esc(it.label ?? "")}</span>`).join("")}</div></div></section>`;
  }
  if (style === "compact") {
    return `<section class="nl-trust-bar nl-trust-bar--compact" data-track-section="trust-strip"><div class="container"><div class="nl-trust-bar-grid">${items.map((it, i) => `<div class="nl-trust-bar-item">${it.icon ? `<i class="bi ${esc(it.icon)}" aria-hidden="true"></i>` : ""}<span class="nl-trust-bar-label">${esc(it.label ?? "")}</span></div>${i < items.length - 1 ? `<span class="nl-trust-bar-divider" aria-hidden="true"></span>` : ""}`).join("")}</div></div></section>`;
  }
  return `<section class="nl-trust-bar" data-track-section="trust-strip"><div class="container"><div class="nl-trust-bar-grid">${items.map((it, i) => `<div class="nl-trust-bar-item reveal reveal-delay-${i + 1}">${it.icon ? `<div class="nl-trust-bar-icon"><i class="bi ${esc(it.icon)}"></i></div>` : ""}<div class="nl-trust-bar-text"><span class="nl-trust-bar-label">${esc(it.label ?? "")}</span></div></div>`).join("")}</div></div></section>`;
}

function renderNeueraCards(p: Record<string, unknown>, vars: Record<string, string>, assetUrl: (id: string) => string): string {
  const cards = Array.isArray(p.cards) ? p.cards as Array<Record<string, unknown>> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  const eyebrow = String(p.eyebrow ?? "");
  const cols = String(p.columns ?? "3");
  const style = String(p.style ?? "default");
  const bg = p.background === "warm" ? " nl-section-warm" : p.background === "dark" ? " nl-section-dark" : "";
  const anchor = p.anchor_id ? ` id="${esc(String(p.anchor_id))}"` : "";
  const headerHtml = (heading || eyebrow) ? `<div class="nl-section-header text-center reveal">${eyebrow ? `<span class="nl-eyebrow">${esc(eyebrow)}</span>` : ""}${heading ? `<h2>${heading}</h2>` : ""}${subheading ? `<p class="nl-section-subhead">${subheading}</p>` : ""}</div>` : "";

  if (style === "compact") {
    const gridCls = cols === "5" ? "nl-card-grid--compact nl-card-grid--5col" : cols === "4" ? "nl-card-grid--compact nl-card-grid--4col" : "nl-card-grid--compact nl-card-grid--3col";
    return `<section class="nl-section${bg}"${anchor} data-track-section="cards-${esc(slugify(heading))}"><div class="container">${headerHtml}<div class="${gridCls}">${cards.map((c, i) => `<div class="nl-card-tile reveal reveal-delay-${i + 1}">${c.icon ? `<div class="nl-card-tile__icon"><i class="bi ${esc(String(c.icon))}" aria-hidden="true"></i></div>` : ""}<div class="nl-card-tile__label">${esc(String(c.title ?? ""))}</div></div>`).join("")}</div></div></section>`;
  }
  const gridCls = cols === "2" ? "nl-grid-2" : cols === "4" ? "nl-grid-4" : "nl-grid-3";
  const tintCycle = ["nl-card-icon-terracotta", "nl-card-icon-sage", "nl-card-icon-lavender"];
  const cardsHtml = cards.map((c, i) => {
    const tint = tintCycle[i % 3];
    const link = (c.link_text && c.link_url) ? `<a href="${esc(String(c.link_url))}" class="nl-btn nl-btn-secondary" style="font-size:0.85rem;padding:0.5rem 1.25rem;" data-track-click="${esc(slugify(String(c.link_text)))}" data-track-category="card" data-track-label="${esc(slugify(String(c.title ?? "")))}">${esc(String(c.link_text))} <i class="bi bi-arrow-right" aria-hidden="true"></i></a>` : "";
    if (style === "numbered") {
      return `<div class="nl-card reveal reveal-delay-${i + 1}"><div class="nl-card-body"><div class="nl-card-number">${i + 1}</div><h3 class="nl-card-title">${esc(String(c.title ?? ""))}</h3>${c.description ? `<p class="nl-card-text">${esc(String(c.description))}</p>` : ""}${link}</div></div>`;
    }
    if (style === "icon") {
      const iconInner = c.icon ? `<i class="bi ${esc(String(c.icon))}" aria-hidden="true"></i>` : c.image ? `<img src="${esc(imgSrc(c.image, assetUrl))}" alt="${esc(String(c.title ?? ""))}" style="width:28px;height:28px;" loading="lazy" />` : `<i class="bi bi-heart-pulse" aria-hidden="true"></i>`;
      return `<div class="nl-card reveal reveal-delay-${i + 1}"><div class="nl-card-body"><div class="nl-card-icon ${tint}">${iconInner}</div><h3 class="nl-card-title">${esc(String(c.title ?? ""))}</h3>${c.description ? `<p class="nl-card-text">${esc(String(c.description))}</p>` : ""}${link}</div></div>`;
    }
    // default
    const imgHtml = c.image ? `<picture><img src="${esc(imgSrc(c.image, assetUrl))}" class="nl-card-img" alt="${esc(String(c.title ?? ""))}" loading="lazy" width="400" height="250" /></picture>` : c.icon ? `<div class="nl-card-body" style="padding-top:1.5rem"><div class="nl-card-icon ${tint}"><i class="bi ${esc(String(c.icon))}" aria-hidden="true"></i></div></div>` : "";
    return `<div class="nl-card reveal reveal-delay-${i + 1}">${imgHtml}<div class="nl-card-body"><h3 class="nl-card-title">${esc(String(c.title ?? ""))}</h3>${c.description ? `<p class="nl-card-text">${esc(String(c.description))}</p>` : ""}${link}</div></div>`;
  }).join("");
  return `<section class="nl-section${bg}"${anchor} data-track-section="cards-${esc(slugify(heading))}"><div class="container">${headerHtml}<div class="${gridCls}">${cardsHtml}</div></div></section>`;
}

function renderFeatures(p: Record<string, unknown>, vars: Record<string, string>): string {
  const features = Array.isArray(p.features) ? p.features as Array<{ icon?: string; title?: string; description?: string }> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  const eyebrow = String(p.eyebrow ?? "");
  const bg = p.background === "warm" ? " nl-section-warm" : p.background === "dark" ? " nl-section-dark" : p.background === "default" ? "" : " nl-section-warm";
  const tintCycle = ["nl-card-icon-terracotta", "nl-card-icon-sage", "nl-card-icon-lavender"];
  return `<section class="nl-section${bg}" data-track-section="features-${esc(slugify(heading))}"><div class="container">${(heading || eyebrow) ? `<div class="nl-section-header text-center reveal">${eyebrow ? `<span class="nl-eyebrow">${esc(eyebrow)}</span>` : ""}${heading ? `<h2>${heading}</h2>` : ""}${subheading ? `<p class="nl-section-subhead">${subheading}</p>` : ""}</div>` : ""}<div class="nl-grid-3">${features.map((f, i) => `<div class="nl-feature reveal reveal-delay-${i + 1}">${f.icon ? `<div class="nl-feature-icon ${tintCycle[i % 3]}"><i class="bi ${esc(f.icon)}"></i></div>` : ""}<h3 class="nl-card-title">${esc(f.title ?? "")}</h3><p class="nl-card-text">${esc(f.description ?? "")}</p></div>`).join("")}</div></div></section>`;
}

function renderTestimonials(p: Record<string, unknown>, vars: Record<string, string>, assetUrl: (id: string) => string): string {
  const ts = Array.isArray(p.testimonials) ? p.testimonials as Array<{ quote?: string; author_name?: string; author_role?: string; author_image?: string }> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const dark = String(p.theme ?? "") === "dark";
  const gridCls = (ts.length % 2 === 0) ? "nl-grid-2" : "nl-grid-3";
  const avatarCycle = ["terracotta", "sage", "lavender"];
  return `<section class="${dark ? "nl-testimonials-dark" : "nl-section nl-section-warm"}" data-track-section="testimonials"><div class="container">${heading ? `<div class="nl-section-header text-center reveal"><span class="nl-eyebrow">${dark ? "Patient stories" : "What our patients say"}</span><h2${dark ? ` style="color:var(--nl-white)"` : ""}>${heading}</h2><div class="nl-divider"${dark ? ` style="opacity:0.3"` : ""}></div></div>` : ""}<div class="${gridCls}">${ts.map((t, i) => `<div class="nl-testimonial-card reveal reveal-delay-${i + 1}"><div class="nl-testimonial-stars">${"<i class=\"bi bi-star-fill\" aria-hidden=\"true\"></i>".repeat(5)}</div><div class="nl-testimonial-quote-icon">&ldquo;</div><p class="nl-testimonial-text">${esc(t.quote ?? "")}</p><div class="nl-testimonial-author">${t.author_image ? `<img src="${esc(imgSrc(t.author_image, assetUrl))}" alt="${esc(t.author_name ?? "")}" class="nl-testimonial-avatar" loading="lazy" />` : `<div class="nl-testimonial-avatar nl-testimonial-avatar-initials nl-avatar-${avatarCycle[i % 3]}">${esc((t.author_name ?? "?").charAt(0))}</div>`}<div><div class="nl-testimonial-name">${esc(t.author_name ?? "")}</div>${t.author_role ? `<div class="nl-testimonial-role">${esc(t.author_role)}</div>` : ""}</div></div></div>`).join("")}</div></div></section>`;
}

function renderSteps(p: Record<string, unknown>, vars: Record<string, string>, assetUrl: (id: string) => string): string {
  const steps = Array.isArray(p.steps) ? p.steps as Array<{ image?: string; step_number?: string; title?: string; description?: string }> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  return `<section class="nl-section nl-section-warm" data-track-section="steps-${esc(slugify(heading))}"><div class="container">${heading ? `<div class="nl-section-header text-center reveal"><h2>${heading}</h2>${subheading ? `<p class="lead">${subheading}</p>` : ""}<div class="nl-divider"></div></div>` : ""}<div class="nl-steps-grid">${steps.map((s, i) => `<div class="nl-step-item reveal reveal-delay-${i + 1}"><div class="nl-step-connector"></div><div class="nl-step-num"><span>${esc(s.step_number ?? String(i + 1))}</span></div>${s.image ? `<img src="${esc(imgSrc(s.image, assetUrl))}" alt="${esc(s.title ?? "")}" class="nl-step-img" loading="lazy" width="400" height="280" />` : ""}<h3 class="nl-step-title">${esc(s.title ?? "")}</h3>${s.description ? `<p class="nl-step-desc">${esc(s.description)}</p>` : ""}</div>`).join("")}</div></div></section>`;
}

function renderFaq(p: Record<string, unknown>, vars: Record<string, string>): string {
  const items = Array.isArray(p.items) ? p.items as Array<{ question?: string; answer?: string }> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  const ld = items.length ? `<script type="application/ld+json">${JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: items.map((it) => ({ "@type": "Question", name: it.question ?? "", acceptedAnswer: { "@type": "Answer", text: String(it.answer ?? "").replace(/<[^>]+>/g, "") } })) })}</script>` : "";
  return `<section class="nl-section" data-track-section="faq-${esc(slugify(heading))}"><div class="container">${heading ? `<div class="text-center mb-5 reveal"><h2>${heading}</h2>${subheading ? `<p class="lead">${subheading}</p>` : ""}</div>` : ""}<div class="row justify-content-center"><div class="col-lg-8">${items.map((it, i) => `<div class="nl-faq-item reveal reveal-delay-${i + 1}"><button class="nl-faq-question" type="button" data-track-click="faq-expand" data-track-category="faq" data-track-label="${esc(slugify(String(it.question ?? "")).slice(0, 50))}"><span>${esc(it.question ?? "")}</span><svg class="nl-faq-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg></button><div class="nl-faq-answer"><div class="nl-faq-answer-inner">${sanitize(String(it.answer ?? ""), vars)}</div></div></div>`).join("")}</div></div></div>${ld}</section>`;
}

function renderCta(p: Record<string, unknown>, vars: Record<string, string>): string {
  const heading = interpolate(String(p.heading ?? ""), vars);
  const description = interpolate(String(p.description ?? ""), vars);
  const style = String(p.style ?? "primary");
  const btnText = String(p.button_text ?? "");
  const btnLink = String(p.button_link ?? "");
  const btn2Text = String(p.secondary_button_text ?? "");
  const btn2Link = String(p.secondary_button_link ?? "");
  const cls = style === "primary" ? "nl-cta-primary" : style === "secondary" ? "nl-cta-secondary" : "nl-cta-outline";
  const trackSlug = slugify(heading) || "bare";
  return `<section class="${cls}${!heading && !description ? " nl-cta--bare" : ""}${p.background === "warm" ? " nl-cta--warm" : ""}" data-track-section="cta-${esc(trackSlug)}">${style === "primary" ? `<div class="nl-cta-dots nl-cta-dots-1"></div><div class="nl-cta-dots nl-cta-dots-2"></div>` : ""}<div class="container position-relative"><div class="nl-cta-inner reveal">${style === "primary" ? `<div class="nl-cta-deco"><i class="bi bi-heart-pulse-fill" aria-hidden="true"></i></div>` : ""}${heading ? `<h2 class="nl-cta-heading">${heading}</h2>` : ""}${description ? `<p class="nl-cta-description">${description}</p>` : ""}<div class="nl-cta-actions nl-cta-stack">${btnText ? `<a href="${esc(!btnLink || btnLink === "#book" ? "#" : btnLink)}" class="nl-btn ${style === "primary" ? "nl-btn-white" : "nl-btn-primary"}" ${(!btnLink || btnLink === "#book") ? "data-open-booking" : ""} data-track-click="BAOC-cta-${esc(trackSlug.slice(0, 20))}" data-track-category="cta" data-track-label="${esc(trackSlug)}"><span>${esc(btnText)}</span></a>` : ""}${btn2Text && btn2Link ? `<a href="${esc(btn2Link)}" class="nl-btn ${style === "primary" ? "nl-btn-outline-white" : "nl-btn-secondary"}" data-track-click="${esc(slugify(btn2Text))}" data-track-category="cta" data-track-label="${esc(trackSlug)}-secondary">${esc(btn2Text)}</a>` : ""}</div>${style === "primary" ? `<div class="nl-cta-trust"><span><i class="bi bi-shield-check"></i> No commitment required</span><span><i class="bi bi-clock"></i> Start in minutes</span><span><i class="bi bi-lock"></i> Private &amp; secure</span></div>` : ""}</div></div></section>`;
}

function renderProgramsList(p: Record<string, unknown>, vars: Record<string, string>): string {
  const programs = Array.isArray(p.programs) ? p.programs as Array<Record<string, unknown>> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  return `<section class="nl-section" data-track-section="programs-list"><div class="container">${heading ? `<div class="nl-section-header text-center reveal"><h2>${heading}</h2>${subheading ? `<p class="nl-section-subhead">${subheading}</p>` : ""}</div>` : ""}<div class="nl-programs-list">${programs.map((pg, i) => `<article class="nl-program-card nl-program-card--${esc(String(pg.color ?? "terracotta"))} reveal reveal-delay-${i + 1}"><div class="nl-program-card-head"><h3 class="nl-program-name">${esc(String(pg.name ?? ""))}</h3>${pg.tagline ? `<p class="nl-program-tagline">${esc(String(pg.tagline))}</p>` : ""}${pg.duration ? `<div class="nl-program-duration"><i class="bi bi-clock"></i> ${esc(String(pg.duration))}</div>` : ""}</div>${pg.description ? `<p class="nl-program-desc">${esc(String(pg.description))}</p>` : ""}${Array.isArray(pg.symptoms) && pg.symptoms.length ? `<div class="nl-program-section"><h4>Common symptoms</h4><ul>${(pg.symptoms as string[]).map((s) => `<li>${esc(s)}</li>`).join("")}</ul></div>` : ""}${Array.isArray(pg.ideal_candidates) && pg.ideal_candidates.length ? `<div class="nl-program-section"><h4>Great for</h4><ul>${(pg.ideal_candidates as string[]).map((s) => `<li>${esc(s)}</li>`).join("")}</ul></div>` : ""}${Array.isArray(pg.whats_included) && pg.whats_included.length ? `<div class="nl-program-section"><h4>What's included</h4><ul>${(pg.whats_included as string[]).map((s) => `<li>${esc(s)}</li>`).join("")}</ul></div>` : ""}${pg.cta_text && pg.cta_link ? `<a href="${esc(String(pg.cta_link))}" class="nl-btn nl-btn-primary">${esc(String(pg.cta_text))} <i class="bi bi-arrow-right"></i></a>` : ""}</article>`).join("")}</div></div></section>`;
}

function renderServices(p: Record<string, unknown>, vars: Record<string, string>): string {
  const cats = Array.isArray(p.categories) ? p.categories as Array<{ category_name?: string; services?: Array<{ name?: string; price?: string; description?: string }> }> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  return `<section class="nl-section" data-track-section="services"><div class="container">${heading ? `<div class="nl-section-header text-center reveal"><h2>${heading}</h2></div>` : ""}<div class="nl-services-list">${cats.map((cat) => `<div class="nl-service-cat"><h3 class="nl-service-cat-name">${esc(cat.category_name ?? "")}</h3><div class="nl-grid-2">${(cat.services ?? []).map((s) => `<div class="nl-service-card"><div class="nl-service-card-head"><h4 class="nl-service-name">${esc(s.name ?? "")}</h4>${s.price ? `<div class="nl-service-price">${esc(s.price)}</div>` : ""}</div>${s.description ? `<p class="nl-service-desc">${esc(s.description)}</p>` : ""}</div>`).join("")}</div></div>`).join("")}</div></div></section>`;
}

function renderPricing(p: Record<string, unknown>, vars: Record<string, string>): string {
  const tiers = Array.isArray(p.tiers) ? p.tiers as Array<Record<string, unknown>> : [];
  const heading = interpolate(String(p.heading ?? ""), vars);
  const subheading = interpolate(String(p.subheading ?? ""), vars);
  return `<section class="nl-section nl-section-warm" data-track-section="pricing"><div class="container">${heading ? `<div class="nl-section-header text-center reveal"><h2>${heading}</h2>${subheading ? `<p class="nl-section-subhead">${subheading}</p>` : ""}</div>` : ""}<div class="nl-pricing-grid">${tiers.map((t, i) => `<article class="nl-pricing-card${t.is_popular ? " nl-pricing-card--popular" : ""} reveal reveal-delay-${i + 1}">${t.is_popular ? `<div class="nl-pricing-badge">Most popular</div>` : ""}<h3 class="nl-pricing-name">${esc(String(t.name ?? ""))}</h3>${t.duration ? `<div class="nl-pricing-duration">${esc(String(t.duration))}</div>` : ""}${t.description ? `<p class="nl-pricing-desc">${esc(String(t.description))}</p>` : ""}${Array.isArray(t.features) && t.features.length ? `<ul class="nl-pricing-features">${(t.features as string[]).map((f) => `<li><i class="bi bi-check2"></i> ${esc(f)}</li>`).join("")}</ul>` : ""}${t.cta_text && t.cta_link ? `<a href="${esc(String(t.cta_link))}" class="nl-btn ${t.is_popular ? "nl-btn-primary" : "nl-btn-outline"}">${esc(String(t.cta_text))}</a>` : ""}</article>`).join("")}</div></div></section>`;
}

function renderOfficeInfo(p: Record<string, unknown>, _vars: Record<string, string>): string {
  const schedule = Array.isArray(p.schedule) ? p.schedule as Array<{ days?: string; hours?: string }> : [];
  const areas = Array.isArray(p.service_areas) ? p.service_areas as Array<{ region?: string; cities?: string }> : [];
  return `<section class="nl-section" data-track-section="office-info"><div class="container"><div class="nl-grid-2">${p.schedule_heading || schedule.length ? `<div class="nl-office-card"><h3>${esc(String(p.schedule_heading ?? "Office hours"))}</h3><ul>${schedule.map((s) => `<li><strong>${esc(s.days ?? "")}</strong> <span>${esc(s.hours ?? "")}</span></li>`).join("")}</ul>${p.schedule_note ? `<p class="nl-office-note">${esc(String(p.schedule_note))}</p>` : ""}</div>` : ""}${p.areas_heading || areas.length ? `<div class="nl-office-card"><h3>${esc(String(p.areas_heading ?? "Service areas"))}</h3><ul>${areas.map((a) => `<li><strong>${esc(a.region ?? "")}</strong> <span>${esc(a.cities ?? "")}</span></li>`).join("")}</ul>${p.areas_note ? `<p class="nl-office-note">${esc(String(p.areas_note))}</p>` : ""}</div>` : ""}</div>${p.disclaimer_heading || p.disclaimer_text ? `<div class="nl-info-banner nl-info-banner--warning"><h4>${esc(String(p.disclaimer_heading ?? ""))}</h4><p>${esc(String(p.disclaimer_text ?? ""))}</p></div>` : ""}</div></section>`;
}

function renderContactForm(p: Record<string, unknown>, vars: Record<string, string>): string {
  const heading = interpolate(String(p.heading ?? ""), vars);
  const description = interpolate(String(p.description ?? ""), vars);
  const submit = String(p.submit_button_text ?? "Send message");
  return `<section class="nl-section" data-track-section="contact-form"><div class="container"><div class="row justify-content-center"><div class="col-lg-8 reveal"><div class="nl-form-wrapper">${heading ? `<h2 class="text-center mb-3">${heading}</h2>` : ""}${description ? `<p class="text-center lead mb-4">${description}</p>` : ""}<form class="contact-form" method="POST" data-success-message="${esc(String(p.success_message ?? "Thanks — we'll be in touch soon."))}" data-track-form="contact-form" novalidate><div class="row g-3"><div class="col-md-6"><div class="nl-form-group"><input type="text" class="nl-form-control" id="cf-name" name="name" required minlength="2" placeholder=" " autocomplete="name" /><label for="cf-name" class="nl-form-label">Full name</label></div></div><div class="col-md-6"><div class="nl-form-group"><input type="email" class="nl-form-control" id="cf-email" name="email" required placeholder=" " autocomplete="email" /><label for="cf-email" class="nl-form-label">Email</label></div></div><div class="col-12"><div class="nl-form-group"><input type="text" class="nl-form-control" id="cf-subject" name="subject" placeholder=" " /><label for="cf-subject" class="nl-form-label">Subject</label></div></div><div class="col-12"><div class="nl-form-group"><textarea class="nl-form-control" id="cf-message" name="message" rows="5" required placeholder=" "></textarea><label for="cf-message" class="nl-form-label">How can we help?</label></div></div></div><button type="submit" class="nl-btn nl-btn-primary mt-3 w-100">${esc(submit)}</button></form></div></div></div></div></section>`;
}

function renderNewsletter(p: Record<string, unknown>, vars: Record<string, string>): string {
  const heading = interpolate(String(p.heading ?? ""), vars);
  const description = interpolate(String(p.description ?? ""), vars);
  const button = String(p.button_text ?? "Subscribe");
  return `<section class="nl-section nl-section-warm" data-track-section="newsletter"><div class="container"><div class="nl-newsletter-wrap reveal">${heading ? `<h2>${heading}</h2>` : ""}${description ? `<p>${description}</p>` : ""}<form class="nl-newsletter-form" data-track-form="newsletter"><input type="email" name="email" placeholder="you@example.com" required class="nl-form-control" /><button type="submit" class="nl-btn nl-btn-primary">${esc(button)}</button></form></div></div></section>`;
}

function fieldHtml(f: { kind: string; name: string; label: string; required?: boolean; placeholder?: string }): string {
  const req = f.required ? "required" : "";
  const ph = f.placeholder ? `placeholder="${escapeHtml(f.placeholder)}"` : "";
  if (f.kind === "textarea") return `<label>${escapeHtml(f.label)}<textarea name="${escapeHtml(f.name)}" class="field" ${ph} ${req}></textarea></label>`;
  if (f.kind === "checkbox") return `<label><input type="checkbox" name="${escapeHtml(f.name)}" ${req} /> ${escapeHtml(f.label)}</label>`;
  const type = ["text", "email", "tel", "url", "number", "date"].includes(f.kind) ? f.kind : "text";
  return `<label>${escapeHtml(f.label)}<input type="${type}" name="${escapeHtml(f.name)}" class="field" ${ph} ${req} /></label>`;
}

function renderMobileOverrides(blocks: Block[], _vars: Record<string, string>, _assetUrl: (id: string) => string): string {
  // For Phase 1 we emit a single @media block that hides blocks marked mobile=false.
  const hidden = blocks.filter((b) => b.visibility?.mobile === false);
  if (hidden.length === 0) return "";
  const selectors = hidden.map((b) => `[data-block-id="${b.id}"]`).join(", ");
  return `@media (max-width: 767px) { ${selectors} { display: none !important; } }`;
}

function sanitize(html: string, vars: Record<string, string>): string {
  // Very conservative sanitization: allow basic tags only.
  const allowed = /<\/?(p|br|strong|em|b|i|u|a|h[1-6]|ul|ol|li|blockquote)(\s+href="[^"]*")?\s*\/?>/gi;
  const interpolated = interpolate(html, vars);
  return interpolated.replace(/<[^>]+>/g, (m) => (allowed.test(m) ? m : ""));
}

// ── Side-files emitted alongside pages ───────────────────────────────────
export function renderSitemap(site: Site, pages: PageDoc[], origin: string): string {
  const urls = pages
    .filter((p) => p.revision && !p.slug.startsWith("__") && !p.revision.metadata.noindex)
    .map((p) => {
      const path = buildPagePath(p, pages).replace(/\/index\.html$/, "/");
      return `  <url><loc>${escapeHtml(origin)}${escapeHtml(path)}</loc></url>`;
    })
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

export function renderRobots(origin: string): string {
  return `User-agent: *\nAllow: /\nSitemap: ${origin}/sitemap.xml\n`;
}

export function renderHeaders(settings?: { cspOverrides?: { scriptSrc?: string[]; styleSrc?: string[]; connectSrc?: string[]; imgSrc?: string[]; frameSrc?: string[] } }): string {
  const csp = buildCsp(settings?.cspOverrides);
  return [
    `/*`,
    `  X-Content-Type-Options: nosniff`,
    `  Referrer-Policy: strict-origin-when-cross-origin`,
    `  Permissions-Policy: geolocation=(), microphone=(), camera=()`,
    `  Strict-Transport-Security: max-age=31536000; includeSubDomains`,
    `  Content-Security-Policy: ${csp}`,
    `  X-Frame-Options: SAMEORIGIN`,
    ``,
  ].join("\n");
}

function buildCsp(overrides?: { scriptSrc?: string[]; styleSrc?: string[]; connectSrc?: string[]; imgSrc?: string[]; frameSrc?: string[] }): string {
  const o = overrides ?? {};
  const list = (extra?: string[]) => ["'self'", ...(extra ?? [])].join(" ");
  return [
    `default-src 'self'`,
    `script-src ${list(o.scriptSrc)} 'unsafe-inline'`,
    `style-src ${list(o.styleSrc)} 'unsafe-inline' https://fonts.googleapis.com`,
    `font-src 'self' https://fonts.gstatic.com data:`,
    `img-src ${list(o.imgSrc)} data: https:`,
    `connect-src ${list(o.connectSrc)} https:`,
    `frame-src ${list(o.frameSrc)}`,
    `object-src 'none'`,
    `base-uri 'self'`,
    `form-action 'self' https:`,
    `frame-ancestors 'self'`,
  ].join("; ");
}

export type PwaIcons = Array<{ src: string; sizes: string; type: string }>;

export function renderPwaManifest(site: { name: string }, theme: Theme | null, icons: PwaIcons = []): string {
  return JSON.stringify({
    name: site.name,
    short_name: site.name.slice(0, 12),
    start_url: "/",
    display: "standalone",
    background_color: theme?.colors?.ground ?? "#ffffff",
    theme_color: theme?.colors?.primary ?? "#2563eb",
    icons,
  }, null, 2);
}

export function renderServiceWorker(): string {
  return `// CMS Plus minimal service worker — offline shell only.
const CACHE = "v1";
self.addEventListener("install", (e) => { self.skipWaiting(); });
self.addEventListener("activate", (e) => { e.waitUntil(self.clients.claim()); });
self.addEventListener("fetch", (e) => {
  if (e.request.method !== "GET") return;
  e.respondWith(caches.match(e.request).then((cached) => cached ?? fetch(e.request).then((r) => {
    if (r.ok && r.status < 400) {
      const copy = r.clone();
      caches.open(CACHE).then((c) => c.put(e.request, copy));
    }
    return r;
  }).catch(() => caches.match("/index.html"))));
});
`;
}

export function renderRedirects(redirects: Array<{ source: string; target: string; status: number }>): string {
  if (redirects.length === 0) return "";
  return redirects.map((r) => `${r.source} ${r.target} ${r.status}`).join("\n") + "\n";
}
